import React, { useState } from 'react';
import { FaRocket, FaShareAlt } from 'react-icons/fa';

const ConfigurationStep = ({ onNext, loading }) => {
    const [formData, setFormData] = useState({
        generalObjective: 'ventas',
        budget: 'medio',
        timeline: '3 meses',
        currentChannels: ['whatsapp', 'instagram', 'facebook']
    });

    const [errors, setErrors] = useState({});

    const objectives = [
        {
            value: 'ventas',
            label: 'Aumentar Ventas',
            description: 'Incrementar el volumen de ventas y el ticket promedio',
            icon: FaRocket,
            color: 'blue'
        },
        {
            value: 'clientes_nuevos',
            label: 'Atraer Nuevos Clientes',
            description: 'Expandir la base de clientes y captar nuevos segmentos',
            icon: FaShareAlt,
            color: 'green'
        },
        {
            value: 'fidelizacion',
            label: 'Fidelizar Clientes',
            description: 'Mejorar la retención y lealtad de clientes existentes',
            icon: FaRocket,
            color: 'purple'
        }
    ];

    const budgets = [
        { value: 'bajo', label: 'Bajo (€100-500/mes)', description: 'Estrategias básicas y orgánicas' },
        { value: 'medio', label: 'Medio (€500-2000/mes)', description: 'Combinación de orgánico y pagado' },
        { value: 'alto', label: 'Alto (€2000+/mes)', description: 'Campañas integrales con publicidad' }
    ];

    const timelines = [
        { value: '1 mes', label: '1 mes' },
        { value: '3 meses', label: '3 meses' },
        { value: '6 meses', label: '6 meses' },
        { value: '12 meses', label: '12 meses' }
    ];

    const channels = [
        { value: 'whatsapp', label: 'WhatsApp', icon: '💬' },
        { value: 'instagram', label: 'Instagram', icon: '📷' },
        { value: 'facebook', label: 'Facebook', icon: '📘' },
        { value: 'email', label: 'Email Marketing', icon: '📧' },
        { value: 'google', label: 'Google Ads', icon: '🔍' },
        { value: 'tiktok', label: 'TikTok', icon: '🎵' }
    ];

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Limpiar error del campo
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleChannelToggle = (channel) => {
        setFormData(prev => ({
            ...prev,
            currentChannels: prev.currentChannels.includes(channel)
                ? prev.currentChannels.filter(c => c !== channel)
                : [...prev.currentChannels, channel]
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.generalObjective) {
            newErrors.generalObjective = 'Debes seleccionar un objetivo';
        }

        if (!formData.budget) {
            newErrors.budget = 'Debes seleccionar un presupuesto';
        }

        if (!formData.timeline) {
            newErrors.timeline = 'Debes seleccionar un timeline';
        }

        if (formData.currentChannels.length === 0) {
            newErrors.currentChannels = 'Debes seleccionar al menos un canal';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            onNext(formData);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Configura tu Estrategia de Marketing
                </h2>
                <p className="text-gray-600">
                    Cuéntanos sobre tus objetivos y recursos para generar una estrategia personalizada
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Objetivo General */}
                <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-4">
                        ¿Cuál es tu objetivo principal?
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {objectives.map((objective) => {
                            const IconComponent = objective.icon;
                            return (
                                <div
                                    key={objective.value}
                                    className={`relative cursor-pointer rounded-lg border-2 p-6 transition-all duration-200 ${formData.generalObjective === objective.value
                                        ? `border-${objective.color}-500 bg-${objective.color}-50`
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    onClick={() => handleInputChange('generalObjective', objective.value)}
                                >
                                    <div className="flex items-center mb-3">
                                        <IconComponent className={`text-${objective.color}-500 text-xl mr-3`} />
                                        <h3 className="font-semibold text-gray-900">{objective.label}</h3>
                                    </div>
                                    <p className="text-sm text-gray-600">{objective.description}</p>
                                    {formData.generalObjective === objective.value && (
                                        <div className={`absolute top-2 right-2 w-6 h-6 bg-${objective.color}-500 rounded-full flex items-center justify-center`}>
                                            <span className="text-white text-sm">✓</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {errors.generalObjective && (
                        <p className="text-red-500 text-sm mt-2">{errors.generalObjective}</p>
                    )}
                </div>

                {/* Presupuesto */}
                <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-4">
                        ¿Cuál es tu presupuesto mensual?
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {budgets.map((budget) => (
                            <div
                                key={budget.value}
                                className={`relative cursor-pointer rounded-lg border-2 p-4 transition-all duration-200 ${formData.budget === budget.value
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                onClick={() => handleInputChange('budget', budget.value)}
                            >
                                <h3 className="font-semibold text-gray-900 mb-1">{budget.label}</h3>
                                <p className="text-sm text-gray-600">{budget.description}</p>
                                {formData.budget === budget.value && (
                                    <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                        <span className="text-white text-sm">✓</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    {errors.budget && (
                        <p className="text-red-500 text-sm mt-2">{errors.budget}</p>
                    )}
                </div>

                {/* Timeline */}
                <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-4">
                        ¿En qué plazo quieres ver resultados?
                    </label>
                    <div className="flex flex-wrap gap-3">
                        {timelines.map((timeline) => (
                            <button
                                key={timeline.value}
                                type="button"
                                className={`px-6 py-3 rounded-lg border-2 transition-all duration-200 ${formData.timeline === timeline.value
                                    ? 'border-blue-500 bg-blue-500 text-white'
                                    : 'border-gray-200 text-gray-700 hover:border-gray-300'
                                    }`}
                                onClick={() => handleInputChange('timeline', timeline.value)}
                            >
                                {timeline.label}
                            </button>
                        ))}
                    </div>
                    {errors.timeline && (
                        <p className="text-red-500 text-sm mt-2">{errors.timeline}</p>
                    )}
                </div>

                {/* Canales Actuales */}
                <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-4">
                        ¿Qué canales utilizas actualmente?
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {channels.map((channel) => (
                            <div
                                key={channel.value}
                                className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${formData.currentChannels.includes(channel.value)
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                onClick={() => handleChannelToggle(channel.value)}
                            >
                                <span className="text-2xl mr-3">{channel.icon}</span>
                                <span className="font-medium text-gray-900">{channel.label}</span>
                                {formData.currentChannels.includes(channel.value) && (
                                    <div className="ml-auto w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                        <span className="text-white text-xs">✓</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    {errors.currentChannels && (
                        <p className="text-red-500 text-sm mt-2">{errors.currentChannels}</p>
                    )}
                </div>

                {/* Botón de Continuar */}
                <div className="pt-6">
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                Analizando tu negocio...
                            </>
                        ) : (
                            <>
                                <FaRocket className="mr-3" />
                                Generar Estrategia Personalizada
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ConfigurationStep; 